// FILE: src/components/modals/ProductDetailsModal.jsx

import React, { useEffect, useState } from 'react';
import { Home, Heart, ShoppingCart, Plus, Minus, Package, Truck, Shield } from 'lucide-react';
import { getProductImage } from '../../utils/imageHelpers';

const ProductDetailsModal = ({ 
  isOpen, 
  onClose, 
  product, 
  addToCart, 
  showToast, 
  favorites = [], 
  toggleFavorite,
  setShowSubscription 
}) => {
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const isFav = Array.isArray(favorites)
    ? favorites.some((item) => String(item?.id) === String(product.id))
    : false;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    showToast && showToast(`${quantity}x ${product.name} added to cart!`, 'success');
  };

  const handleFavoriteToggle = () => {
    if (typeof toggleFavorite === 'function') {
      toggleFavorite(product);
    }
  };

  const getWeightParts = () => {
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) {
        return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
      }
    }
    return { value: null, unit: product.unit || '' };
  };

  const formatQuantityLabel = (qty) => {
    const { value, unit } = getWeightParts();
    if (value) {
      const total = (value * qty).toFixed(2).replace(/\.00$/, '');
      const unitLabel = unit ? ` ${unit}` : '';
      return `${qty} (${total}${unitLabel})`;
    }
    return `${qty}`;
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    const normalizedUnit = String(unit).toLowerCase().trim();
    return ['kg', 'gram', 'g'].includes(normalizedUnit);
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = () => {
    const { unit } = getWeightParts();
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1,2,3,4];
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-50 page-overlay-scroll">
      <div className="w-full max-w-md mx-auto min-h-full bg-gray-50">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-b-3xl">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
              title="Go back"
            >
              <Home size={22} />
            </button>
          </div>
          <h1 className="text-2xl font-bold">Product Details</h1>
          <p className="text-blue-100">Review product info before adding to cart</p>
        </div>

        <div className="p-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Product Image */}
            <div className="px-6 pt-4">
              <div className="relative">
                {getProductImage(product, 'modal')}
                <button
                  onClick={handleFavoriteToggle}
                  className="absolute top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:scale-105 transition-transform"
                >
                  <Heart size={20} className={`${isFav ? 'text-pink-500' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>

            {/* Product Info */}
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-800 mb-2 font-display">{product.name}</h3>
                <div className="flex items-center gap-2 mb-3">
                  {product.sale_price && product.sale_price < product.price ? (
                    <>
                      <span className="text-2xl font-extrabold text-green-600 drop-shadow-sm">₹{product.sale_price}</span>
                      <span className="text-lg text-gray-400 line-through">₹{product.price}</span>
                      <span className="text-sm bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600 text-white px-3 py-1 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
                        {Math.round(((product.price - product.sale_price) / product.price) * 100)}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-extrabold text-emerald-600 drop-shadow-sm">₹{product.price}</span>
                  )}
                  {product.fat && (
                    <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                      {product.fat} fat
                    </span>
                  )}
                  {product.weight && !product.fat && (
                    <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                      {product.weight}{product.unit ? ` ${formatUnit(product.unit)}` : ''}
                    </span>
                  )}
                  {product.brand && !product.fat && !product.weight && (
                    <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                      {product.brand}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Description</h4>
                <p className="text-gray-600 leading-relaxed text-base">
                  {product.description || 'Fresh and high-quality product delivered directly to your doorstep.'}
                </p>
              </div>

              {/* Product Features */}
              <div className="mb-6 bg-green-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Features</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Shield size={16} className="text-green-600" />
                    </div>
                    <span className="text-sm">100% Fresh & Natural</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Truck size={16} className="text-blue-600" />
                    </div>
                    <span className="text-sm">Free Home Delivery</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <Package size={16} className="text-purple-600" />
                    </div>
                    <span className="text-sm">Hygienic Packaging</span>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Quantity</h4>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  {getQuantityOptions().map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => setQuantity(qty)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        quantity === qty
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                      }`}
                    >
                      {qty}
                    </button>
                  ))}
                  {isWeightUnit(getWeightParts().unit) && (
                    <span className="text-xs text-gray-600 ml-2 font-medium">Kg</span>
                  )}
                  {!isWeightUnit(getWeightParts().unit) && getWeightParts().unit && (
                    <span className="text-xs text-gray-600 ml-2 font-medium">{formatUnit(getWeightParts().unit)}</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-gray-50 transition-colors"
                      disabled={quantity <= 1}
                    >
                      <Minus size={16} className={quantity <= 1 ? 'text-gray-300' : 'text-gray-600'} />
                    </button>
                    <span className="px-4 py-3 font-medium text-gray-800 border-l border-r border-gray-300">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-gray-50 transition-colors"
                    >
                      <Plus size={16} className="text-gray-600" />
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    Total: ₹{((product.sale_price && product.sale_price < product.price ? product.sale_price : product.price) * quantity).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-blue-500 text-white py-4 rounded-xl text-base font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={20} />
                  Add {quantity > 1 ? `${quantity} items` : 'to Cart'} - ₹{((product.sale_price && product.sale_price < product.price ? product.sale_price : product.price) * quantity).toFixed(2)}
                </button>

                {setShowSubscription && Boolean(Number(product.is_subscribable)) && (
                  <button
                    onClick={() => {
                      setShowSubscription(product);
                      onClose();
                    }}
                    className="w-full bg-green-500 text-white py-4 rounded-xl text-base font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                  >
                    🔔 Subscribe & Save 10%
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;